import React, { useState, useCallback, useEffect } from 'react'
import { Form, Button } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../../actions'
import MaterialInput from '../../../common/MaterialInput'
import $t from '../../../../i18n'
import config from '../../../../config.json'
import { event, eventTypes } from '../../../../consts/eventConsts'
import PageCard from '../../../common/PageCard'
import Page from '../../../common/Page/Page'

import loginImage from '../../../../resources/login.svg'
import Dialog from '../../../common/Dialog'
import { routes } from '../../../../consts'

import './SigninForm.css'

const SigninForm = () => {
  const dispatch = useDispatch()

  const [form] = Form.useForm()

  const email = useSelector(state => state.signup?.userSignup?.email)
  const { errorMessage, isLoginLoading } = useSelector(state => state.auth)
  const [error, setError] = useState(null)

  const isThToHmoEnrolled = useSelector(state => state.auth?.isThToHmoEnrolled)
  const [isThToHmoEnrolledSignUp, setIsThToHmoEnrolledSignUp] =
    useState(isThToHmoEnrolled)

  useEffect(() => {
    setError(errorMessage)
  }, [errorMessage])

  const handleDontHaveAnAccount = () => {
    dispatch(Actions.createAction(Actions.DONT_HAVE_AN_ACCOUNT))
  }

  const handleLogin = useCallback(data => {
    const { password, logInEmail } = data
    dispatch(
      Actions.createAction(Actions.SIGNIN_REQUESTED, {
        email: email || logInEmail,
        password,
        element: 'signin-form',
      })
    )
  }, [])

  const redirectToLoginWithOneTimeCode = () => {
    dispatch(Actions.createAction(Actions.REDIRECT_TO_ONE_TIME_LOGIN))
  }

  const handleForgetPassword = () => {
    dispatch(Actions.createAction(Actions.FORGET_PASSWORD_REQUESTED))
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        element: 'Forgot password',
      })
    )
  }

  const renderLoginEmailField = () => (
    <Form.Item
      name="logInEmail"
      type="text"
      className="form-item-email"
      rules={[
        {
          required: true,
          type: 'email',
          message: $t('pages.getStarted.invalidEmail'),
        },
      ]}
    >
      <MaterialInput
        type="text"
        className="signin-form-email"
        label={$t('pages.getStarted.signinForm.email')}
        name="logInEmail"
      />
    </Form.Item>
  )

  const renderLoginPasswordField = () => (
    <Form.Item
      name="password"
      type="text"
      className="form-item-password"
      rules={[
        {
          required: true,
          message: $t(
            'pages.getStarted.signupForm.errors.pleaseInputYourPassword'
          ),
        },
      ]}
    >
      <MaterialInput
        className="signin-form-password"
        type="password"
        label={$t('pages.getStarted.signinForm.password')}
      />
    </Form.Item>
  )

  const renderSigninButtton = () => (
    <Form.Item className="form-item-signin-button">
      <Button
        className="form-buttton signin-button"
        type="primary"
        loading={isLoginLoading}
        htmlType="submit"
      >
        {$t('pages.getStarted.signinForm.signIn')}
      </Button>
    </Form.Item>
  )

  const renderLoginWithOneTimeCodeButton = () => (
    <Button
      className="form-buttton login-with-otp-button"
      type="secondary"
      onClick={redirectToLoginWithOneTimeCode}
    >
      {$t('pages.getStarted.signinForm.logInWithOneTimeCode')}
    </Button>
  )

  const renderTermsAndConditionsStatement = () => (
    <div className="terms-and-conditions-statement">
      {$t('pages.getStarted.signinForm.bySigningInYouAgreeWithThe')}
      <a
        data-name={'Terms of use link'}
        href={config.termsURL}
        rel="noreferrer"
        target="_blank"
        className="signin-form-tandc trackable link"
      >
        {$t('pages.getStarted.signinForm.termsOfUse')}
      </a>
      {$t('pages.getStarted.signinForm.and')}
      <a
        data-name={'Privacy Policy link'}
        href={config.privacyURL}
        target="_blank"
        rel="noreferrer"
        className="signin-form-privacy-policy trackable link"
      >
        {$t('pages.getStarted.signinForm.privacyPolicy')}
      </a>
    </div>
  )

  const renderForgetPassword = () => (
    <div className="forget-password-wrapper">
      <div
        className="forget-password trackable"
        data-name="forget password"
        onClick={handleForgetPassword}
      >
        {$t('pages.getStarted.signinForm.forgetPassword')}
      </div>
    </div>
  )

  const getSubtitle = () => {
    return (
      <div className="signin-form-subtitle">
        {$t('pages.getStarted.signinForm.subtitle')}
        <Button
          type="link"
          data-name="Sign-up button"
          onClick={handleDontHaveAnAccount}
          className="Sign-up-button trackable"
        >
          {$t('pages.getStarted.signupForm.signUp')}
        </Button>
      </div>
    )
  }

  return (
    <PageCard className="login-page-card" switchLanguage={true}>
      <Page
        hideAlertBanner={true}
        title={$t('pages.getStarted.signinForm.title')}
        subtitle={getSubtitle()}
        className="login-page center"
      >
        <img src={loginImage} className="login-img" alt="" />
        <Form
          id="signin-form"
          className="signin-form trackable"
          data-name="Sign-in"
          onFinish={handleLogin}
          onFinishFailed={error => {
            dispatch(
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.invalid,
                element: 'signin-form',
                data: error.values,
                error: error,
              })
            )
          }}
          name="basic"
          onValuesChange={() => setError(null)}
          initialValues={{
            password: '',
            logInEmail: email,
          }}
          form={form}
        >
          <div className="signin-form-fields">
            {renderLoginEmailField()}
            {renderLoginPasswordField()}
            {renderForgetPassword()}
            {error && <div className="signin-server-errors">{error}</div>}
          </div>
          <div className="signin-form-buttons-container">
            {renderSigninButtton()}
            {renderLoginWithOneTimeCodeButton()}
          </div>
        </Form>
        {renderTermsAndConditionsStatement()}
        <Dialog
          title={$t('dialogs.thToHmoEnrollrdSignUp.title')}
          isVisible={isThToHmoEnrolledSignUp}
          className="thToHmoEnrollrdSignUp-dialog"
          footerActions={[
            <Button
              key={'signup'}
              type="primary"
              onClick={() => {
                setIsThToHmoEnrolledSignUp(false)
                dispatch(
                  Actions.createAction(Actions.NAVIGATED_TO, routes.SIGN_UP)
                )
              }}
            >
              {$t('dialogs.thToHmoEnrollrdSignUp.signUp')}
            </Button>,
          ]}
          closable={false}
          maskClosable={false}
          onCancel={() => setIsThToHmoEnrolledSignUp(false)}
        >
          <div className="thToHmoEnrollrdSignUp-text">
            {$t('dialogs.thToHmoEnrollrdSignUp.subtitle')}
          </div>
        </Dialog>
      </Page>
    </PageCard>
  )
}

export default SigninForm
